/**
 * "Usuários": the organisation's people and its open invitations, each a
 * section of cards -- both belong to a tenant admin's one job here (who has
 * access) and neither is worth hiding behind a tab.
 *
 * The frame also draws a "Pedidos de acesso" card (unsolicited access
 * requests); there is no such concept anywhere in this API or the platform
 * design spec, so it is not rendered rather than invented client-side.
 *
 * No status line here. Inviting colleagues costs no credit and is never
 * gated (§18.3), so a balance line would attach the waiting state to the
 * one screen it does not govern — which is how a status line becomes chrome
 * and stops being read where it matters.
 *
 * A 403 on the member list must not blank the page: `MemberList` renders its
 * own alert through `QueryStates`, and invitations -- a separate
 * authorisation -- render regardless of whether members loaded.
 */

import type { ReactNode } from 'react';
import { useParams } from '@tanstack/react-router';
import { UserPlus } from 'lucide-react';

import { Page, PageHeader } from '../../../chrome/page';
import { Button } from '../../../ui/button';
import { InviteDialog } from '../invitations/InviteForm';
import { InvitationList } from '../invitations/InvitationList';
import { MemberList } from '../members/MemberList';
import { tt } from '../i18n';

export function Team(): ReactNode {
  // `useParams({ from })` needs a registered route *id*, and the composition
  // nests every feature route under a pathless `shell` layout route whose own
  // id prefixes every descendant's -- so the literal URL path is never a
  // registered id. Every sibling page in this area reads params loosely for
  // the same reason (TenantHome, NewCampaign, RequestCredits).
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };

  return (
    <Page>
      <PageHeader
        title={tt('team.heading')}
        actions={
          <InviteDialog
            tenantId={tenantId}
            trigger={
              <Button type="button">
                <UserPlus aria-hidden className="size-4" />
                {tt('invite.trigger')}
              </Button>
            }
          />
        }
      />
      <div className="flex flex-col gap-8">
        <MemberList tenantId={tenantId} />
        <InvitationList tenantId={tenantId} />
      </div>
    </Page>
  );
}
